import { createFileRoute, useLocation, useNavigate, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Logo } from "@/components/core/logo";
import { ArrowLeft } from "lucide-react";
import {
  REFERRAL_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from "@/config/base";
import { formatPhoneInput } from "@/lib/utils";
import { showToast } from "@/hooks/useToast";
import { HttpError } from "@/lib/http-client";

export const Route = createFileRoute("/auth/identify")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (token && user) {
      throw redirect({
        to: "/app",
      });
    }
  },
  component: RouteComponent,
});

const phoneSchema = z.object({
  telefone: z
    .string()
    .min(1, "Informe um número de telefone")
    .refine(
      (value) => value.replace(/[^0-9]+/g, "").length >= 10,
      "Informe um número com DDD"
    ),
});

function RouteComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithPhone, loginWithGoogle, requestCode } = useAuth();
  const ref =
    typeof (location.search as Record<string, unknown>)?.ref === "string"
      ? ((location.search as Record<string, unknown>).ref as string)
      : "";

  const [isLoading, setIsLoading] = useState(false);
  const [phoneStep, setPhoneStep] = useState<"form" | "code">("form");
  const [phoneCode, setPhoneCode] = useState("");

  useEffect(() => {
    if (!ref) {
      return;
    }

    const normalizedCode = ref.trim().toUpperCase();
    if (/^[A-Z0-9]{8}$/.test(normalizedCode)) {
      sessionStorage.setItem(REFERRAL_STORAGE_KEY, normalizedCode);
    }
  }, [ref]);

  const phoneForm = useForm<z.infer<typeof phoneSchema>>({
    // @ts-ignore
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      telefone: "",
    },
  });

  const phoneValue = phoneForm.watch("telefone") ?? "";
  const sanitizedPhone = useMemo(
    () => phoneValue.replace(/[^0-9]+/g, ""),
    [phoneValue]
  );

  const phoneDisplay = useMemo(() => {
    if (!sanitizedPhone) return "";
    return formatPhoneInput(sanitizedPhone);
  }, [sanitizedPhone]);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      setIsLoading(false);
      let errorMessage = "Não foi possível fazer login com Google";

      if (err instanceof HttpError) {
        errorMessage = err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      showToast.error(errorMessage, { duration: 5000 });
    }
  };

  const handleRequestPhoneCode = phoneForm.handleSubmit(async () => {
    setIsLoading(true);
    try {
      await requestCode({ telefone: sanitizedPhone });
      showToast.success("Código enviado! Verifique seu telefone.");
      setPhoneStep("code");
      setPhoneCode("");
    } catch (err) {
      let errorMessage = "Não foi possível enviar o código";

      if (err instanceof HttpError) {
        errorMessage = err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      showToast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  });

  const handleConfirmPhoneCode = async () => {
    setIsLoading(true);
    try {
      const authData = await loginWithPhone({
        telefone: sanitizedPhone,
        codigo: phoneCode,
      });

      if (authData?.access) {
        showToast.success("Login realizado com sucesso!");
        navigate({ to: "/app" });
        return;
      }

      if ((authData?.requiresEmailVerification || !authData?.user?.emailVerificado) && authData.user) {
        // @ts-ignore - O campo de search nao ta sendo reconhecido pelo TypeScript
        navigate({ to: "/auth/wait-verify-email", search: { email: authData.user.email } });
        return;
      }

      if ((authData?.requiresPhoneVerification || !authData?.user?.telefoneVerificado) && authData.user) {
        navigate({ to: "/auth/verify-phone", search: { telefone: authData.user.telefone } });
        return;
      }

      // Fallback
      showToast.error("Não foi possível concluir o login", { duration: 5000 });
    } catch (err) {
      let errorMessage = "Código inválido";

      if (err instanceof HttpError) {
        errorMessage = err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      showToast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8">
          <div className="flex items-center justify-center mb-6">
            <Logo height={40} isTraditionalLogo />
          </div>

          {phoneStep === "form" ? (
            <div className="space-y-4">
              <Form {...phoneForm}>
                <form onSubmit={handleRequestPhoneCode} className="space-y-4">
                  <FormField
                    control={phoneForm.control}
                    name="telefone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefone cadastrado</FormLabel>
                        <FormControl>
                          <Input
                            inputMode="tel"
                            placeholder="(00) 00000-0000"
                            value={field.value}
                            onChange={(event) =>
                              field.onChange(
                                formatPhoneInput(event.target.value)
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? "Enviando..." : "Receber código"}
                  </Button>
                </form>
              </Form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Ou</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={isLoading}
                onClick={handleGoogleLogin}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Entrar com Google
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-1 text-sm text-gray-600">
                <p className="text-center">Enviamos um código para <br/> {phoneDisplay}</p>
                <p className="text-center">Informe o código abaixo para entrar.</p>
              </div>

              <InputOTP
                maxLength={6}
                value={phoneCode}
                onChange={(value) => setPhoneCode(value)}
                containerClassName="justify-center"
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <InputOTPSlot key={index} index={index} />
                  ))}
                </InputOTPGroup>
              </InputOTP>

              <div className="space-y-2">
                <Button
                  type="button"
                  className="w-full"
                  disabled={phoneCode.length !== 6 || isLoading}
                  onClick={handleConfirmPhoneCode}
                >
                  {isLoading ? "Confirmando..." : "Confirmar código"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={isLoading}
                  onClick={() => {
                    setPhoneStep("form");
                    setPhoneCode("");
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Alterar telefone
                </Button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-xs text-gray-400">
            Ao continuar, você concorda com os termos de uso e a política de
            privacidade da JaPediu.
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => {
                const storedRefCode = sessionStorage.getItem(REFERRAL_STORAGE_KEY) ?? "";

                navigate({
                  to: "/auth/register",
                  search: storedRefCode ? { ref: storedRefCode } : {},
                });
              }}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              Não tem uma conta?{" "}
              <span className="font-semibold text-blue-600">Cadastre-se</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
